import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadCloud, Download, X, CheckCircle2, AlertCircle, XCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ICON = {
  ok: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  invalid: <XCircle className="w-4 h-4 text-red-600" />,
  duplicate: <AlertCircle className="w-4 h-4 text-amber-600" />,
};

const STATUS_LABEL = {
  ok: 'Ready',
  invalid: 'Invalid',
  duplicate: 'Skipped',
};

/**
 * Bulk driver import — upload CSV → preview → commit.
 * Renders inline (parent controls visibility via `open` prop).
 */
const BulkDriverImportModal = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadSample = async () => {
    try {
      const res = await axios.get(`${API}/drivers/bulk-import/sample`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'drivers-import-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    }
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    setPreviewing(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await axios.post(`${API}/drivers/bulk-import/preview`, fd, {
        withCredentials: true,
      });
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to parse file');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview || preview.summary.ok === 0) return;
    setCommitting(true);
    const okRows = preview.rows
      .filter((r) => r.status === 'ok')
      .map((r) => ({ email: r.email, name: r.name || undefined, phone: r.phone || undefined }));
    try {
      const res = await axios.post(
        `${API}/drivers/bulk-import/commit`,
        { rows: okRows },
        { withCredentials: true }
      );
      setResult(res.data);
      toast.success(`Imported ${res.data.created_count} drivers`);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-driver-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Bulk Import Drivers
            </h2>
            <p className="text-sm text-slate-500">Upload a CSV to invite up to 500 drivers at once.</p>
          </div>
          <button
            data-testid="bulk-driver-close-btn"
            onClick={handleClose}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </header>

        <div className="p-6 overflow-y-auto flex-1">
          {!result && (
            <>
              <div className="flex items-center justify-between mb-4">
                <button
                  data-testid="bulk-driver-sample-btn"
                  onClick={downloadSample}
                  className="flex items-center gap-2 text-sm text-[#002FA7] hover:underline"
                >
                  <Download className="w-4 h-4" />
                  Download CSV template
                </button>
                <div className="text-xs text-slate-500">Columns: email (required), name, phone</div>
              </div>

              <label
                htmlFor="bulk-driver-file"
                data-testid="bulk-driver-dropzone"
                className="block border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#002FA7] hover:bg-blue-50/30 transition"
              >
                <UploadCloud className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-700">
                  {file ? file.name : 'Click to upload a CSV file'}
                </p>
                <p className="text-xs text-slate-500 mt-1">Max 5 MB, up to 500 rows</p>
                <input
                  id="bulk-driver-file"
                  data-testid="bulk-driver-file-input"
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleFile}
                  className="hidden"
                  disabled={previewing || committing}
                />
              </label>

              {previewing && (
                <div className="mt-4 flex items-center gap-2 text-slate-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Parsing…
                </div>
              )}

              {preview && !previewing && (
                <div className="mt-6" data-testid="bulk-driver-preview">
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">Total</div>
                      <div className="text-2xl font-bold text-slate-900">{preview.summary.total}</div>
                    </div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="bulk-driver-summary-ok">
                      <div className="text-xs text-emerald-700 uppercase tracking-wide">Ready</div>
                      <div className="text-2xl font-bold text-emerald-900">{preview.summary.ok}</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="text-xs text-amber-700 uppercase tracking-wide">Skipped</div>
                      <div className="text-2xl font-bold text-amber-900">{preview.summary.duplicate}</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="text-xs text-red-700 uppercase tracking-wide">Invalid</div>
                      <div className="text-2xl font-bold text-red-900">{preview.summary.invalid}</div>
                    </div>
                  </div>

                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wider">
                        <tr>
                          <th className="px-3 py-2 text-left">Row</th>
                          <th className="px-3 py-2 text-left">Email</th>
                          <th className="px-3 py-2 text-left">Name</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r) => (
                          <tr key={r.row_number} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-500">{r.row_number}</td>
                            <td className="px-3 py-2 font-mono text-xs">{r.email || '—'}</td>
                            <td className="px-3 py-2">{r.name || '—'}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1.5">
                                {STATUS_ICON[r.status]}
                                {STATUS_LABEL[r.status]}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-slate-600">
                              {r.errors.join(', ') || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {result && (
            <div data-testid="bulk-driver-result">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Import complete</h3>
                  <p className="text-sm text-slate-600">
                    Invited <strong>{result.created_count}</strong> drivers.
                    {result.skipped_count > 0 && ` Skipped ${result.skipped_count}.`}
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-700 mb-3">
                Each new driver received a unique invitation link. Share it via email or SMS from the Drivers list.
              </p>
            </div>
          )}
        </div>

        <footer className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3 bg-slate-50">
          {!result && (
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                data-testid="bulk-driver-commit-btn"
                onClick={handleCommit}
                disabled={!preview || preview.summary.ok === 0 || committing}
                className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#00247D] text-white text-sm font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {committing && <Loader2 className="w-4 h-4 animate-spin" />}
                {preview ? `Import ${preview.summary.ok} drivers` : 'Import'}
              </button>
            </>
          )}
          {result && (
            <button
              onClick={handleClose}
              className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-[#00247D] text-white text-sm font-semibold"
            >
              Done
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default BulkDriverImportModal;
